import { X } from "lucide-react";

import { DatePicker } from "@/components/date-picker";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { StageCombobox } from "@/features/stages/components/stage-combobox";
import { useStages } from "@/features/stages/hooks/use-stages.ts";
import { useMembers } from "@/features/team/hooks/use-members.ts";
import { dateOnlyToDate, dateToDateOnly } from "@/lib/format";
import { useActiveContext } from "@/store/use-active-context";

const ALL_AUTHORS = "todos";

export interface FeedFilterValues {
	stageId?: string;
	occurredFrom?: string;
	occurredTo?: string;
	authorUserId?: string;
}

export function hasActiveFilters(value: FeedFilterValues): boolean {
	return !!(
		value.stageId ||
		value.occurredFrom ||
		value.occurredTo ||
		value.authorUserId
	);
}

interface FeedFilterBarProps {
	value: FeedFilterValues;
	onChange: (next: FeedFilterValues) => void;
}

export function FeedFilterBar({ value, onChange }: FeedFilterBarProps) {
	const activeObraId = useActiveContext((state) => state.activeObraId);
	const stagesQuery = useStages(activeObraId ?? undefined);
	const membersQuery = useMembers();

	const stages = stagesQuery.data?.stages ?? [];
	const members = membersQuery.data ?? [];

	const fromDate = dateOnlyToDate(value.occurredFrom);
	const toDate = dateOnlyToDate(value.occurredTo);

	function patch(partial: Partial<FeedFilterValues>) {
		onChange({ ...value, ...partial });
	}

	return (
		<div className="feed-filter-bar">
			<div className="feed-filter-bar__field feed-filter-bar__field--stage">
				<span className="feed-filter-bar__label">Etapa</span>
				<StageCombobox
					stages={stages}
					value={value.stageId ?? ""}
					onChange={(next) => patch({ stageId: next || undefined })}
					placeholder="Todas as etapas"
					noneLabel="Todas as etapas"
					labelFromValue
				/>
			</div>
			<div className="feed-filter-bar__field">
				<span className="feed-filter-bar__label">De</span>
				<DatePicker
					selected={fromDate}
					onSelected={(next) =>
						patch({ occurredFrom: next ? dateToDateOnly(next) : undefined })
					}
					calendarProps={toDate ? { disabled: { after: toDate } } : undefined}
				/>
			</div>
			<div className="feed-filter-bar__field">
				<span className="feed-filter-bar__label">Até</span>
				<DatePicker
					selected={toDate}
					onSelected={(next) =>
						patch({ occurredTo: next ? dateToDateOnly(next) : undefined })
					}
					calendarProps={
						fromDate ? { disabled: { before: fromDate } } : undefined
					}
				/>
			</div>
			<div className="feed-filter-bar__field feed-filter-bar__field--author">
				<span className="feed-filter-bar__label">Autor</span>
				<Select
					value={value.authorUserId ?? ALL_AUTHORS}
					onValueChange={(next) =>
						patch({ authorUserId: next === ALL_AUTHORS ? undefined : next })
					}
				>
					<SelectTrigger className="feed-filter-bar__author-trigger">
						<SelectValue placeholder="Todos os autores" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={ALL_AUTHORS}>Todos os autores</SelectItem>
						{members.map((member) => (
							<SelectItem key={member.userId} value={member.userId}>
								{member.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			{hasActiveFilters(value) && (
				<Button
					variant="ghost"
					size="sm"
					className="feed-filter-bar__clear"
					onClick={() => onChange({})}
				>
					<X /> Limpar filtros
				</Button>
			)}
		</div>
	);
}
